// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

import { GradesState } from './interfaces';
import { GradesActionsTypes } from '../actions/grades-actions';
import { AiGradesActionTypes } from '../actions/ai-grades-actions';
import { mapGradeValue } from '../utils/grades';
import { EMPTY_AI_GRADES } from '../utils/ai-grades';

const defaultState: GradesState = {
    ai: EMPTY_AI_GRADES,
    aiModels: {
        loaded: false,
        loading: false,
        canGenerate: false,
        canSeeSpend: false,
        autoModels: [],
        providers: null,
        picks: {},
    },
    aiSpend: null,
    grades_loading: false,
    generate_robogrades_loading: false,
    update_human_grades_loading: false,
    error: null,
    warning: null,
    values: {
        front_centering_human_grade: 0,
        front_corners_human_grade: 0,
        front_edges_human_grade: 0,
        front_surface_human_grade: 0,
        back_centering_human_grade: 0,
        back_corners_human_grade: 0,
        back_edges_human_grade: 0,
        back_surface_human_grade: 0,
        front_centering_laser_grade: 0,
        front_corners_laser_grade: 0,
        front_edges_laser_grade: 0,
        front_surface_laser_grade: 0,
        back_centering_laser_grade: 0,
        back_corners_laser_grade: 0,
        back_edges_laser_grade: 0,
        back_surface_laser_grade: 0,
        front_overall_predicted_grade: 0,
        back_overall_predicted_grade: 0,
        front_overall_from_subgrades: 0,
        back_overall_from_subgrades: 0,
    },
};

export default function (state: GradesState = defaultState, action: any): GradesState {
    switch (action.type as GradesActionsTypes | AiGradesActionTypes) {
        case AiGradesActionTypes.LOAD_AI_MODELS:
            return { ...state, aiModels: { ...state.aiModels, loading: true } };
        case AiGradesActionTypes.LOAD_AI_MODELS_SUCCESS:
            return {
                ...state,
                aiModels: {
                    ...state.aiModels,
                    loaded: true,
                    loading: false,
                    canGenerate: action.payload.canGenerate,
                    canSeeSpend: action.payload.canSeeSpend,
                    autoModels: action.payload.autoModels,
                    providers: action.payload.providers,
                    picks: action.payload.picks,
                },
            };
        case AiGradesActionTypes.LOAD_AI_MODELS_FAILED:
            return {
                ...state,
                aiModels: { ...state.aiModels, loaded: true, loading: false },
            };
        case AiGradesActionTypes.PICK_AI_MODEL:
            return {
                ...state,
                aiModels: {
                    ...state.aiModels,
                    picks: { ...state.aiModels.picks, [action.payload.provider]: action.payload.pick },
                },
            };
        case AiGradesActionTypes.RESET_AI_GRADES:
            return { ...state, ai: { ...EMPTY_AI_GRADES, certificateId: action.payload.certificateId } };
        // an answer about another card than the one on screen is dropped
        case AiGradesActionTypes.AI_GRADES_LOADED:
            if (action.payload.certificateId !== state.ai.certificateId) {
                return state;
            }
            return {
                ...state,
                ai: {
                    ...state.ai, ...action.payload.card, loaded: true, error: null,
                },
            };
        case AiGradesActionTypes.AI_GRADES_FAILED:
            if (action.payload.certificateId !== state.ai.certificateId) {
                return state;
            }
            return { ...state, ai: { ...state.ai, loaded: true, error: action.payload.error } };
        case AiGradesActionTypes.AI_GRADES_RUN_REQUESTED:
            return { ...state, ai: { ...state.ai, starting: [...state.ai.starting, action.payload.provider] } };
        case AiGradesActionTypes.AI_GRADES_RUN_REQUEST_ENDED:
            return {
                ...state,
                ai: {
                    ...state.ai,
                    starting: state.ai.starting.filter((provider) => provider !== action.payload.provider),
                },
            };
        case AiGradesActionTypes.AI_SPEND_LOADED:
            return { ...state, aiSpend: action.payload.spend };
        case GradesActionsTypes.SET_ERROR:
            return { ...state, error: action.payload.error };
        case GradesActionsTypes.SET_WARNING:
            return { ...state, warning: action.payload.warning };
        case GradesActionsTypes.SET_GRADES_LOADING:
            return { ...state, grades_loading: action.payload.grades_loading };
        case GradesActionsTypes.SET_GENERATE_ROBOGRADES_LOADING:
            return { ...state, generate_robogrades_loading: action.payload.generate_robogrades_loading };
        case GradesActionsTypes.SET_UPDATE_HUMAN_GRADES_LOADING:
            return { ...state, update_human_grades_loading: action.payload.update_human_grades_loading };
        case GradesActionsTypes.LOAD_VALUES:
            return {
                ...state,
                values: {
                    ...state.values,
                    ...action.payload.values,
                },
            };
        case GradesActionsTypes.ASSIGN_VALUES:
            return {
                ...state,
                values: {
                    ...state.values,
                    ...action.payload.values,
                },
            };
        case GradesActionsTypes.UPDATE_VALUE:
            return {
                ...state,
                values: {
                    ...state.values,
                    [action.payload.key]: mapGradeValue(action.payload.value),
                },
            };
        default:
            return state;
    }
}
